import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { environmentSlice, socialSlice, governanceSlice, generalSlice, sustainabilityStorySlice } from "@/slices";
import { validateGeneralInfo } from "@/components/workflow/general/generalInfoValidation";
import type {
  AccountabilityData,
  ApplicabilityChecks,
  BiodiversityData,
  CommunityContributionData,
  DiversityInclusionData,
  DocumentMetadata,
  ElectricityEntry,
  EmployeeSupportData,
  EthicsData,
  FuelEntry,
  GeneralInfo,
  HumanRightsData,
  LeadershipData,
  PollutionData,
  PolicyTopicData,
  RefrigerantEntry,
  RiskManagementData,
  Scope3Data,
  SocialData,
  TransitionMeasure,
  TransparencyData,
  WasteData,
  WaterData,
  StoryGeneralData,
  StoryEnvironmentData,
  StorySocialData,
  StoryGovernanceData,
} from "./types";

type TabName = "general" | "environment" | "social" | "governance" | "sustainability-story";

interface UseWizardAutoSaveInput {
  assessmentId: string | null;
  activeTab: TabName;
  isLocked: boolean;
  hydrationState: {
    general: { isHydrating: boolean; isHydrated: boolean };
    environment: { isHydrating: boolean; isHydrated: boolean };
    social: { isHydrating: boolean; isHydrated: boolean };
    governance: { isHydrating: boolean; isHydrated: boolean };
    "sustainability-story": { isHydrating: boolean; isHydrated: boolean };
  };
  onSaveSuccess?: () => void;
  generalInfo: GeneralInfo;
  descriptionOfActivities: string;
  hasSustainabilityPolicies: "yes" | "no" | null;
  sustainabilityPolicyDescription: string;
  sustainabilityPolicyDocuments: DocumentMetadata[];
  policyTopics: PolicyTopicData[];
  showSuggestions: boolean;
  fuelEntries: FuelEntry[];
  electricityEntries: ElectricityEntry[];
  refrigerantEntries: RefrigerantEntry[];
  applicabilityChecks: ApplicabilityChecks;
  pollutionData: PollutionData;
  biodiversityData: BiodiversityData;
  biodiversitySupportingDocuments: DocumentMetadata[];
  waterData: WaterData;
  waterSupportingDocuments: DocumentMetadata[];
  wasteData: WasteData;
  wasteSupportingDocuments: DocumentMetadata[];
  carbonSupportingDocuments: DocumentMetadata[];
  scope3Data: Scope3Data;
  employeeSupportData: EmployeeSupportData;
  humanRightsData: HumanRightsData;
  diversityInclusionData: DiversityInclusionData;
  communityContributionData: CommunityContributionData;
  socialData: SocialData;
  ethicsData: EthicsData;
  transparencyData: TransparencyData;
  leadershipData: LeadershipData;
  riskManagementData: RiskManagementData;
  accountabilityData: AccountabilityData;
  // Kept as three separate per-tab props (rather than one merged value) because all three
  // reducers expose their slice under the same field name (transitionMeasuresByTopic) —
  // spreading all three into one object in WizardContext's getCurrentTabData() would have
  // later spreads silently overwrite earlier ones.
  environmentTransitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
  socialTransitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
  governanceTransitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
  storyGeneralData: StoryGeneralData;
  storyEnvironmentData: StoryEnvironmentData;
  storySocialData: StorySocialData;
  storyGovernanceData: StoryGovernanceData;
}

export function useWizardAutoSave({
  assessmentId,
  activeTab,
  isLocked,
  hydrationState,
  onSaveSuccess,
  generalInfo,
  descriptionOfActivities,
  hasSustainabilityPolicies,
  sustainabilityPolicyDescription,
  sustainabilityPolicyDocuments,
  policyTopics,
  showSuggestions,
  fuelEntries,
  electricityEntries,
  refrigerantEntries,
  applicabilityChecks,
  pollutionData,
  biodiversityData,
  biodiversitySupportingDocuments,
  waterData,
  waterSupportingDocuments,
  wasteData,
  wasteSupportingDocuments,
  carbonSupportingDocuments,
  scope3Data,
  employeeSupportData,
  humanRightsData,
  diversityInclusionData,
  communityContributionData,
  socialData,
  ethicsData,
  transparencyData,
  leadershipData,
  riskManagementData,
  accountabilityData,
  environmentTransitionMeasuresByTopic,
  socialTransitionMeasuresByTopic,
  governanceTransitionMeasuresByTopic,
  storyGeneralData,
  storyEnvironmentData,
  storySocialData,
  storyGovernanceData,
}: UseWizardAutoSaveInput) {
  const { toast } = useToast();
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveInFlightRef = useRef(false);
  const lastValidationErrorRef = useRef<string | null>(null);
  const lastValidationWarningRef = useRef<string | null>(null);

  // Track baseline signature per tab (only for hydrated tabs)
  const baselineSignatureRef = useRef<Record<TabName, string | null>>({
    general: null,
    environment: null,
    social: null,
    governance: null,
    "sustainability-story": null,
  });

  // Track the signature of data that last caused a save failure per tab.
  // Prevents infinite retry loops: if the data hasn't changed since the failure, skip the save.
  const frozenSignatureRef = useRef<Record<TabName, string | null>>({
    general: null,
    environment: null,
    social: null,
    governance: null,
    "sustainability-story": null,
  });

  // Get current tab's data and compute its signature
  const getCurrentTabData = useCallback(() => {
    switch (activeTab) {
      case "general": {
        const state = {
          generalInfo,
          descriptionOfActivities,
          hasSustainabilityPolicies,
          sustainabilityPolicyDescription,
          sustainabilityPolicyDocuments,
          policyTopics,
          suggestionsDismissed: !showSuggestions,
        };
        return {
          slice: generalSlice,
          state,
          signature: generalSlice.computeSignature(state),
        };
      }
      case "environment": {
        const state = {
          fuelEntries,
          electricityEntries,
          refrigerantEntries,
          carbonSupportingDocuments,
          applicabilityChecks,
          pollutionData,
          biodiversityData,
          biodiversitySupportingDocuments,
          waterData,
          waterSupportingDocuments,
          wasteData,
          wasteSupportingDocuments,
          totalEmployees: generalInfo.totalEmployees,
          scope3Data,
          homeworkingPercent: generalInfo.homeworkingPercent,
          riskManagementData,
          transitionMeasuresByTopic: environmentTransitionMeasuresByTopic,
        };
        return {
          slice: environmentSlice,
          state,
          signature: environmentSlice.computeSignature(state),
        };
      }
      case "social": {
        const state = {
          employeeSupportData,
          humanRightsData,
          diversityInclusionData,
          communityContributionData,
          socialData,
          transitionMeasuresByTopic: socialTransitionMeasuresByTopic,
        };
        return {
          slice: socialSlice,
          state,
          signature: socialSlice.computeSignature(state),
        };
      }
      case "governance": {
        const state = {
          ethicsData,
          transparencyData,
          leadershipData,
          accountabilityData,
          transitionMeasuresByTopic: governanceTransitionMeasuresByTopic,
        };
        return {
          slice: governanceSlice,
          state,
          signature: governanceSlice.computeSignature(state),
        };
      }
      case "sustainability-story": {
        const state = {
          storyGeneralData,
          storyEnvironmentData,
          storySocialData,
          storyGovernanceData,
        };
        return {
          slice: sustainabilityStorySlice,
          state,
          signature: sustainabilityStorySlice.computeSignature(state),
        };
      }
      default:
        return null;
    }
  }, [
    activeTab,
    generalInfo,
    descriptionOfActivities,
    hasSustainabilityPolicies,
    sustainabilityPolicyDescription,
    sustainabilityPolicyDocuments,
    policyTopics,
    showSuggestions,
    fuelEntries,
    electricityEntries,
    refrigerantEntries,
    carbonSupportingDocuments,
    applicabilityChecks,
    pollutionData,
    biodiversityData,
    biodiversitySupportingDocuments,
    waterData,
    waterSupportingDocuments,
    wasteData,
    scope3Data,
    wasteSupportingDocuments,
    employeeSupportData,
    humanRightsData,
    diversityInclusionData,
    communityContributionData,
    socialData,
    ethicsData,
    transparencyData,
    leadershipData,
    riskManagementData,
    accountabilityData,
    environmentTransitionMeasuresByTopic,
    socialTransitionMeasuresByTopic,
    governanceTransitionMeasuresByTopic,
    storyGeneralData,
    storyEnvironmentData,
    storySocialData,
    storyGovernanceData,
  ]);

  // Auto-save function - only saves the current active tab
  const autoSave = useCallback(async () => {
    if (!assessmentId || isLocked || saveInFlightRef.current) return;

    const currentTabHydration = hydrationState[activeTab];
    
    // Guard: Wait for current tab to be hydrated
    if (currentTabHydration.isHydrating || !currentTabHydration.isHydrated) return;
    
    // Guard: Don't save until baseline is set for this tab
    if (baselineSignatureRef.current[activeTab] === null) return;

    const tabData = getCurrentTabData();
    if (!tabData) return;

    const { slice, state, signature: currentSignature } = tabData;
    const baselineSignature = baselineSignatureRef.current[activeTab];

    // Check if current tab has changes
    if (currentSignature === baselineSignature) {
      return; // No changes detected
    }

    // Guard: Don't retry a save that already failed with this exact data state
    if (currentSignature === frozenSignatureRef.current[activeTab]) {
      return;
    }

    // Validate general info if saving general tab
    if (activeTab === "general") {
      const issues = validateGeneralInfo(generalInfo);
      const errors = issues.filter(i => i.severity === "error");
      const warnings = issues.filter(i => i.severity === "warning");

      if (errors.length > 0) {
        const errorMessage = errors.map(e => e.message).join("; ");
        if (lastValidationErrorRef.current !== errorMessage) {
          lastValidationErrorRef.current = errorMessage;
          toast({
            title: "Cannot save - Data validation failed",
            description: errorMessage,
            variant: "destructive",
            duration: 5000
          });
        }
        return; // Skip save if validation errors
      } else {
        lastValidationErrorRef.current = null;
      }

      console.log(`[AutoSave] Validation passed for general tab with ${warnings.length} warnings`);
      if (warnings.length > 0) {
        const warningMessage = warnings.map(w => w.message).join("; ");
        if (lastValidationWarningRef.current !== warningMessage) {
          lastValidationWarningRef.current = warningMessage;
          toast({
            title: "Warning",
            description: warningMessage,
            variant: "warning",
            duration: 5000
          });
        }
      } else {
        lastValidationWarningRef.current = null;
      }
    }

    setSaveStatus("saving");
    saveInFlightRef.current = true;

    try {
      // Save only the current tab's data with proper type
      if (activeTab === "general") {
        await generalSlice.save(assessmentId, state as any);
      } else if (activeTab === "environment") {
        await environmentSlice.save(assessmentId, state as any);
      } else if (activeTab === "social") {
        await socialSlice.save(assessmentId, state as any);
      } else if (activeTab === "governance") {
        await governanceSlice.save(assessmentId, state as any);
      } else if (activeTab === "sustainability-story") {
        await sustainabilityStorySlice.save(assessmentId, state as any);
      }

      // Update baseline signature for this tab
      baselineSignatureRef.current[activeTab] = currentSignature;
      frozenSignatureRef.current[activeTab] = null;

      setSaveStatus("saved");
      setLastSaved(new Date());

      // Invalidate completion query if this slice impacts completion
      if (slice.impactsCompletion && onSaveSuccess) {
        onSaveSuccess();
      }

      setTimeout(() => setSaveStatus("idle"), 2000);
      return true;
    } catch (err) {
      console.error(`Auto-save failed for ${activeTab}:`, err);
      frozenSignatureRef.current[activeTab] = currentSignature;
      setSaveStatus("idle");
      toast({
        title: "Save failed",
        description: err instanceof Error ? err.message : "Please try again",
        variant: "destructive"
      });
      return false;
    } finally {
      saveInFlightRef.current = false;
    }
  }, [
    assessmentId,
    activeTab,
    isLocked,
    hydrationState,
    getCurrentTabData,
    generalInfo,
    onSaveSuccess,
    toast,
  ]);

  // Force save - can be called manually to save immediately
  // Returns true if the save succeeded, false if it failed (error toast already shown by autoSave)
  const forceSave = useCallback(async (): Promise<boolean|undefined> => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    // Clear frozen signature so explicit user action always attempts the save
    frozenSignatureRef.current[activeTab] = null;
    return autoSave();
  }, [activeTab, autoSave]);

  // Clear baseline when switching tabs (force re-establishment on next render)
  // MUST run BEFORE the set-baseline effect so that the set-baseline effect
  // can immediately re-set it when returning to an already-hydrated tab.
  // Also clear frozen signature so returning to a tab gets a fresh save attempt.
  useEffect(() => {
    baselineSignatureRef.current[activeTab] = null;
    frozenSignatureRef.current[activeTab] = null;
  }, [activeTab]);

  // Set baseline signature when the current tab is hydrated and baseline is null.
  // This covers two cases:
  // 1. First hydration of the tab (isHydrated becomes true after fetch)
  // 2. Returning to a previously-hydrated tab (clear effect above sets it null first,
  //    then this effect immediately re-sets it from the current state)
  useEffect(() => {
    const currentTabHydration = hydrationState[activeTab];
    if (currentTabHydration.isHydrated && baselineSignatureRef.current[activeTab] === null) {
      const tabData = getCurrentTabData();
      if (tabData) {
        baselineSignatureRef.current[activeTab] = tabData.signature;
      }
    }
  }, [activeTab, hydrationState, getCurrentTabData]);

  // Trigger auto-save when current tab's data changes
  useEffect(() => {
    if (!assessmentId) return;

    const currentTabHydration = hydrationState[activeTab];
    
    // Wait for current tab to be hydrated
    if (currentTabHydration.isHydrating || !currentTabHydration.isHydrated) {
      return;
    }

    // Wait for baseline to be set for this tab
    if (baselineSignatureRef.current[activeTab] === null) {
      return;
    }

    const tabData = getCurrentTabData();
    if (!tabData) return;

    const { signature: currentSignature } = tabData;
    const baselineSignature = baselineSignatureRef.current[activeTab];

    // Check if current tab has changes
    const hasChanges = currentSignature !== baselineSignature;

    if (!hasChanges) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    console.log(`[AutoSave] Change detected in ${activeTab}, scheduling save in 2s`);
    
    // Schedule auto-save with 2-second debounce
    autoSaveTimeoutRef.current = setTimeout(() => {
      console.log(`[AutoSave] Executing save for ${activeTab}`);
      autoSave();
    }, 2000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [
    assessmentId,
    activeTab,
    hydrationState,
    getCurrentTabData,
    autoSave,
  ]);

  return { saveStatus, lastSaved, forceSave };
}
